import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { useEffect, useRef, useState } from 'react';
import { useStateInRef } from 'seagull';
import { useIoContext, useStore, useStoreActions, useStoreStatic } from '~/contexts';
import { FeatureFlag } from '~/featureFlags';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useRootDevice } from '~/providers/DeviceProvider';
import { selectors } from '~/store/state-selectors';
import { secondsUntilJwtStale, STALE_JWT_THRESHOLD_SECONDS } from './intercom-jwt-staleness';
import { planIntercomSession } from './intercomLifecycle';
import { reportMissingIntercomUserHash, reportUnusableIntercomJwt } from './intercomSessionReporting';
export const useIntercomSession = ()=>{
    const userInfo = useStore((state)=>state.userInfo);
    const isRealUser = useStore(selectors.isRealUser);
    const businessId = useStore(selectors.user.businessId) || '';
    const featureFlagsLoaded = useStore((s)=>selectors.ff.featureFlags(s).data != null);
    const { getState } = useStoreStatic();
    const { intercomClient, getNow } = useIoContext();
    const { refreshIntercomJwt } = useStoreActions();
    const featureFlags = useFeatureFlags();
    const rootDevice = useRootDevice();
    const [enableIntercom, setEnableIntercom] = useState(false);
    const useJwtIdentity = featureFlags[FeatureFlag.INTERCOM_JWT_IDENTITY] === true;
    const userEmail = (userInfo === null || userInfo === void 0 ? void 0 : userInfo.email()) || '';
    const userId = (userInfo === null || userInfo === void 0 ? void 0 : userInfo.id()) || '';
    const lastModeRef = useRef('none');
    const getRootDevice = useStateInRef(rootDevice);
    const getEnableIntercom = useStateInRef(enableIntercom);
    const getFeatureFlagsLoaded = useStateInRef(featureFlagsLoaded);
    const getStartSession = useStateInRef(startSession);
    const getRequestSupportClickHeal = useStateInRef(requestSupportClickHeal);
    useEffect(()=>{
        let cancelled = false;
        const plan = planIntercomSession({
            isRealUser,
            enabledForRoute: enableIntercom,
            featureFlagsLoaded,
            useJwtIdentity,
            lastMode: lastModeRef.current
        });
        if (plan.action === 'shutdown') {
            intercomClient.shutdown();
            lastModeRef.current = 'shutdown';
        }
        if (plan.action === 'start') {
            void getStartSession()({
                identity: {
                    userId,
                    email: userEmail
                },
                mode: plan.mode,
                shutdownTiming: plan.shutdownFirst ? 'before-refresh' : 'none',
                alwaysRefreshJwt: false,
                reportIdentityProblems: true,
                isCancelled: ()=>cancelled
            });
        }
        return ()=>{
            cancelled = true;
        };
    }, [
        userEmail,
        userId,
        businessId,
        isRealUser,
        enableIntercom,
        useJwtIdentity,
        featureFlagsLoaded
    ]);
    useEffect(()=>{
        if (isRealUser && featureFlagsLoaded) {
            intercomClient.enableSupportClickDiagnostics({
                requestHeal: ()=>getRequestSupportClickHeal()(),
                canHeal: ()=>mayStartSession(),
                getUserId: ()=>{
                    var _getState_userInfo;
                    return (_getState_userInfo = getState().userInfo) === null || _getState_userInfo === void 0 ? void 0 : _getState_userInfo.id();
                }
            });
        }
    }, [
        isRealUser,
        featureFlagsLoaded
    ]);
    async function startSession(param) {
        let { identity, mode, shutdownTiming, alwaysRefreshJwt, reportIdentityProblems, isCancelled } = param;
        var _selectors_intercomUserJwt;
        if (shutdownTiming === 'before-refresh') {
            intercomClient.shutdown();
        }
        const bootParams = {
            userId: identity.userId,
            email: identity.email,
            hideDefaultLauncher: getRootDevice().isMobileDevice
        };
        if (mode === 'legacy') {
            const userHash = selectors.intercomUserHash(getState());
            if (!userHash && reportIdentityProblems) {
                reportMissingIntercomUserHash(identity);
            }
            if (shutdownTiming === 'before-boot') {
                intercomClient.shutdown();
                lastModeRef.current = 'shutdown';
            } else {
                lastModeRef.current = 'legacy';
            }
            await intercomClient.boot(_object_spread_props(_object_spread({}, bootParams), {
                userHash: userHash || undefined
            }));
            lastModeRef.current = 'legacy';
            return;
        }
        if (!alwaysRefreshJwt) {
            const storedJwt = selectors.intercomUserJwt(getState());
            if (storedJwt == null) {
                intercomClient.reportBootSkipped('no_jwt', identity.userId || undefined);
                lastModeRef.current = 'shutdown';
                return;
            }
            const staleness = secondsUntilJwtStale(storedJwt, selectors.intercomUserJwtReceivedAt(getState()), getNow().getTime());
            if (staleness == null || staleness >= STALE_JWT_THRESHOLD_SECONDS) {
                lastModeRef.current = 'jwt';
                await intercomClient.boot(_object_spread_props(_object_spread({}, bootParams), {
                    jwt: storedJwt
                }));
                return;
            }
        }
        if (shutdownTiming !== 'before-boot') {
            lastModeRef.current = 'jwt';
        }
        const refreshResult = await refreshIntercomJwt();
        if (isCancelled()) {
            return;
        }
        const refreshedJwt = (_selectors_intercomUserJwt = selectors.intercomUserJwt(getState())) !== null && _selectors_intercomUserJwt !== void 0 ? _selectors_intercomUserJwt : undefined;
        const refreshedStaleness = secondsUntilJwtStale(refreshedJwt, selectors.intercomUserJwtReceivedAt(getState()), getNow().getTime());
        if (reportIdentityProblems) {
            if (refreshedStaleness == null || refreshedStaleness < STALE_JWT_THRESHOLD_SECONDS) {
                reportUnusableIntercomJwt({
                    refreshResult,
                    secondsUntilExpiry: refreshedStaleness,
                    identity
                });
            }
        } else if (refreshedJwt == null) {
            return;
        }
        if (shutdownTiming === 'before-boot') {
            intercomClient.shutdown();
            lastModeRef.current = 'shutdown';
        }
        await intercomClient.boot(_object_spread_props(_object_spread({}, bootParams), {
            jwt: refreshedJwt
        }));
        lastModeRef.current = 'jwt';
    }
    function mayStartSession() {
        return selectors.isRealUser(getState()) && getEnableIntercom() && getFeatureFlagsLoaded();
    }
    async function requestSupportClickHeal() {
        const currentUserInfo = getState().userInfo;
        if (currentUserInfo == null || !mayStartSession()) {
            return;
        }
        await startSession({
            identity: {
                userId: currentUserInfo.id() || '',
                email: currentUserInfo.email() || ''
            },
            mode: useJwtIdentity ? 'jwt' : 'legacy',
            shutdownTiming: 'before-boot',
            alwaysRefreshJwt: useJwtIdentity,
            reportIdentityProblems: false,
            isCancelled: ()=>false
        });
    }
    return {
        setEnableIntercom
    };
};
